import * as React from "react";
import { Box, Card, Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import { NewspaperIcon } from "@phosphor-icons/react";

import type { LeadPreview } from "@/hooks/api/use-lead-finder";

import { tint } from "./tint";

const PHONE: Record<LeadPreview["phone"], string> = {
	both: "Direct & mobile",
	direct: "Direct line",
	mobile: "Mobile",
	none: "None",
};

const initials = (name: string) =>
	name
		.split(/\s+/)
		.filter((part) => /^[A-Za-z]/.test(part) && !/^Dr\.?$/i.test(part))
		.slice(0, 2)
		.map((part) => part[0].toUpperCase())
		.join("");

/** "14 Oct", with the year when it is not this one. Read in UTC: a bare date is midnight UTC, a day early in the Americas. */
function day(date: string) {
	const when = new Date(date);
	if (Number.isNaN(when.getTime())) return "";
	const year = when.getUTCFullYear() === new Date().getUTCFullYear() ? undefined : "numeric";
	return when.toLocaleDateString(undefined, { day: "numeric", month: "short", year, timeZone: "UTC" });
}

/** The company's news the search asked for: why now is a good time to call. */
function News({ event }: { event: NonNullable<LeadPreview["event"]> }) {
	return (
		<Tooltip title={event.text} placement="bottom-start">
			<Box sx={{ mt: 0.5, maxWidth: 320 }}>
				<Stack
					direction="row"
					spacing={0.5}
					sx={[{ alignItems: "center", width: "max-content", px: 0.75, borderRadius: "6px" }, tint("primary")]}
				>
					<NewspaperIcon size={12} />
					<Typography variant="caption" sx={{ fontWeight: 500, color: "inherit" }}>
						{[event.kind, day(event.date)].filter(Boolean).join(" · ")}
					</Typography>
				</Stack>
				<Typography
					variant="caption"
					color="text.secondary"
					sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}
				>
					{event.text}
				</Typography>
			</Box>
		</Tooltip>
	);
}

/** A small coloured label, readable on light and dark. */
function Badge({ tone, children }: { tone: "success" | "error" | "primary" | "neutral"; children: React.ReactNode }) {
	return (
		<Box
			component="span"
			sx={[
				{
					display: "inline-block",
					fontSize: 12,
					fontWeight: 500,
					px: 1,
					py: "3px",
					borderRadius: "6px",
					whiteSpace: "nowrap",
				},
				tint(tone),
			]}
		>
			{children}
		</Box>
	);
}

export interface LeadPreviewTableProps {
	total: number;
	leads: LeadPreview[];
}

export function LeadPreviewTable({ total, leads }: LeadPreviewTableProps): React.JSX.Element {
	return (
		<Card sx={{ borderRadius: "16px" }}>
			<Stack
				direction="row"
				spacing={2}
				sx={{ px: 3, py: 2, alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", rowGap: 0.5 }}
			>
				<Typography variant="h6" sx={{ fontWeight: 600, fontSize: 16 }}>
					Free preview
				</Typography>
				<Typography variant="body2" color="text.secondary">
					The {Math.min(leads.length, total)} most reachable of {total.toLocaleString()} · numbers and emails come with
					the leads you get
				</Typography>
			</Stack>
			<Box sx={{ overflowX: "auto" }}>
				<Table size="small" sx={{ minWidth: 720 }}>
					<TableHead>
						<TableRow>
							<TableCell sx={{ pl: 3 }}>Person</TableCell>
							<TableCell>Company</TableCell>
							<TableCell>Reach</TableCell>
							<TableCell>Data quality</TableCell>
							<TableCell sx={{ pr: 3 }}>Status</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{leads.map((lead) => {
							const left = !lead.callable || lead.inAccount;
							return (
								<TableRow key={lead.id} sx={{ opacity: left ? 0.55 : 1 }}>
									<TableCell sx={{ pl: 3, py: 1.5 }}>
										<Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
											<Box
												aria-hidden
												sx={[
													{
														width: 36,
														height: 36,
														flexShrink: 0,
														borderRadius: 999,
														fontSize: 13,
														fontWeight: 600,
														display: "flex",
														alignItems: "center",
														justifyContent: "center",
													},
													tint("primary"),
												]}
											>
												{initials(lead.name) || "?"}
											</Box>
											<Box sx={{ minWidth: 0 }}>
												<Typography variant="body2" sx={{ fontWeight: 500 }}>
													{lead.name || "-"}
												</Typography>
												<Typography variant="caption" color="text.secondary">
													{lead.title || "-"}
												</Typography>
											</Box>
										</Stack>
									</TableCell>
									<TableCell>
										<Typography variant="body2">{lead.company || "-"}</Typography>
										{lead.event && <News event={lead.event} />}
									</TableCell>
									<TableCell>
										{lead.callable || lead.phone === "none" ? (
											<Badge tone={lead.phone === "none" ? "neutral" : "success"}>{PHONE[lead.phone]}</Badge>
										) : (
											<Badge tone="error">Do Not Call</Badge>
										)}
									</TableCell>
									<TableCell>
										{lead.accuracy === null ? (
											"-"
										) : (
											<Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
												<Box
													sx={{
														width: 64,
														height: 6,
														borderRadius: 999,
														bgcolor: "var(--mui-palette-background-level1)",
														overflow: "hidden",
													}}
												>
													<Box sx={{ height: "100%", width: `${lead.accuracy}%`, bgcolor: "success.main" }} />
												</Box>
												<Typography variant="caption" sx={{ fontVariantNumeric: "tabular-nums" }}>
													{lead.accuracy}
												</Typography>
											</Stack>
										)}
									</TableCell>
									<TableCell sx={{ pr: 3 }}>
										{lead.inAccount ? (
											<Badge tone="neutral">In your contacts</Badge>
										) : lead.callable ? (
											<Badge tone="primary">New</Badge>
										) : (
											<Badge tone="neutral">Left out</Badge>
										)}
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			</Box>
		</Card>
	);
}
